<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import Popover from 'primevue/popover'
import ToggleSwitch from 'primevue/toggleswitch'

import IconBell from '@/assets/svg/iconBell.svg'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useNotificationSettingsStore } from '@/stores/useNotificationSettingsStore'

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })

const { t } = useI18n()
const settings = useNotificationSettingsStore()
const panel = ref()

const MUTED_COLOR = '#B2B2B2'
const ACTIVE_COLOR = '#9770E5'

const togglePanel = (event: Event) => {
  panel.value?.toggle(event)
}

// El color dice el estado sin abrir el panel: gris cuando hay algo silenciado.
const bellColor = computed(() => (settings.hasMutedNotifications ? MUTED_COLOR : ACTIVE_COLOR))

const tooltip = computed(() =>
  settings.hasMutedNotifications
    ? t('header.notifications.muted_tooltip')
    : t('header.actions.notifications'),
)

const queuedChatSound = computed({
  get: () => settings.queuedChatSound,
  set: (value: boolean) => settings.setQueuedChatSound(value),
})

const queuedChatDesktop = computed({
  get: () => settings.queuedChatDesktop,
  set: (value: boolean) => settings.setQueuedChatDesktop(value),
})
</script>

<template>
  <div>
    <AppButton
      v-tooltip.bottom="tooltip"
      severity="secondary"
      size="small"
      outlined
      rounded
      class="shadow-md"
      :block="false"
      :style="{ color: bellColor, borderColor: bellColor }"
      :class="
        compact
          ? 'app-button-header-glass !h-9 !w-9 !min-w-9 !p-0'
          : 'app-button-header-glass min-w-10  hover:bg-white/50! dark:hover:bg-[var(--p-primary-600)]/50!  text-xs! min-h-10 transition-all duration-200 hover:scale-105 active:scale-95'
      "
      @click="togglePanel"
    >
      <template #icon-start>
        <IconBell :class="compact ? 'h-5 w-5' : 'w-4 h-4'" :stroke-width="1.5" />
      </template>
    </AppButton>

    <Popover
      ref="panel"
      class="!rounded-lg !border !border-white/40 !bg-white/20 !shadow-xl !backdrop-blur-xl !backdrop-saturate-150 dark:!border-[var(--p-primary-color)]/30 dark:!bg-slate-900/40 [&_.p-popover-content]:!bg-transparent"
    >
      <div class="w-64 p-1">
        <p class="mb-1 text-sm font-semibold text-[var(--p-primary-color)]">
          {{ t('header.notifications.title') }}
        </p>
        <p class="mb-3 text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
          {{ t('header.notifications.queued_chats_description') }}
        </p>

        <label class="flex cursor-pointer items-center justify-between gap-3 py-1.5">
          <span class="text-sm text-[var(--p-surface-700)] dark:text-[var(--p-surface-50)]">
            {{ t('header.notifications.queued_chats_sound') }}
          </span>
          <ToggleSwitch v-model="queuedChatSound" />
        </label>

        <label class="flex cursor-pointer items-center justify-between gap-3 py-1.5">
          <span class="text-sm text-[var(--p-surface-700)] dark:text-[var(--p-surface-50)]">
            {{ t('header.notifications.queued_chats_desktop') }}
          </span>
          <ToggleSwitch v-model="queuedChatDesktop" />
        </label>

        <p class="mt-3 text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
          {{ t('header.notifications.own_chats_hint') }}
        </p>
      </div>
    </Popover>
  </div>
</template>
